import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Input } from '../Input';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { EmojiIcon } from '../EmojiIcon';
import { MemorySymbolV1 } from '../MemorySymbolV1';
import CustomLink from '../CustomLink';
import { PRIVACY_POLICY_LINK } from '../../shared/links';
import { useSendResults } from '../../shared/use-send-results';
import { BLOCKS } from '../../shared/memory-map-data';
import { SIGNS, SIGN_VALUES, getMemoryMap } from '../../shared/memory-map-helpers';
import styles from './EmailSlideV10.css';

function EmailSlideV10({
    setEmail,
    onContinue,
    results,
    spriteVersion,
    legalLinks = { privacyPolicy: PRIVACY_POLICY_LINK }
}) {
    const emailRef = useRef(null);
    const map = getMemoryMap(results.userResponses);
    const afterSuccessfulSendResults = callback => {
        document.getElementById('root').scrollIntoView({ block: 'start', behavior: 'smooth' });

        callback(); /* eslint-disable-line */
        onContinue();
    };

    const { fields, changeField, isPending, submitForm, errors } = useSendResults({
        saveEmail: setEmail,
        afterCallback: afterSuccessfulSendResults,
        initialPrelander: results?.version
    });
    const isDidYouMean = Boolean(errors.form?.didYouMean);

    useEffect(() => {
        setTimeout(() => {
            emailRef.current?.focus();
        }, 50);
    }, []);

    return (
        <form
            className={styles.root}
            onSubmit={submitForm}
        >
            <h1 className={styles.title}>You’re one step away from what your memory kept</h1>
            <div className={styles.lockCard} aria-hidden='true'>
                <div className={styles.lockInner}>
                    <div className={styles.lockHead}>
                        <p className={styles.lockTitle}>Your Memory Map</p>
                        <span className={styles.pill}>
                            <EmojiIcon name='lock' version={spriteVersion} className={styles.pillIcon} />
                            Locked
                        </span>
                    </div>
                    <div className={styles.blurred}>
                        <div className={styles.summary}>
                            <div className={styles.totalRing}>
                                <span className={styles.total}>{map.total}</span>
                            </div>
                            <p className={styles.summaryText}>
                                {'You stirred up '}<b>{`${map.total} memories`}</b>{' in 3 minutes. Thousands more are still in there.'}
                            </p>
                        </div>
                        {BLOCKS.map((block, index) => (
                            <div className={styles.row} key={block.key}>
                                <span className={styles.symbol}>
                                    <MemorySymbolV1
                                        type={SIGNS[index]}
                                        value={SIGN_VALUES[SIGNS[index]][map.ch[index].state - 1]}
                                        size={index === 0 ? 60 : 42}
                                    />
                                </span>
                                <span>
                                    <b className={styles.rowName}>{block.name}</b>
                                    <small className={styles.rowNote}>{index === map.top ? 'heaviest, ready to open' : 'ready to open'}</small>
                                </span>
                                <span className={styles.rowChapter}>{`Ch. ${index + 1}`}</span>
                            </div>
                        ))}
                    </div>
                </div>
                <div className={styles.fade} />
            </div>
            <Input
                value={fields.email}
                name='email'
                onChange={changeField}
                className={styles.input}
                placeholder='you@example.com'
                errorText={errors.email || (!isDidYouMean && errors.form?.message)}
                disabled={isPending}
                inputProps={{ autoComplete: 'email', type: 'email', inputMode: 'email' }}
                emailDomainSuggestions={{
                    list: ['gmail.com', 'yahoo.com', 'icloud.com', 'hotmail.com', 'aol.com', 'outlook.com', 'comcast.net', 'att.net', 'live.com', 'mail.com', 'msn.com']
                }}
                inputRef={emailRef}
                didYouMean={errors.form?.didYouMean}
            />
            <div className={styles.agreementBlock}>
                <Icon name='lockSolid' />
                <span>
                    {'We’ll use your email to save your map and send you updates about Boundly. You can unsubscribe at any time. See our '}
                    <CustomLink
                        to={legalLinks.privacyPolicy}
                        target='_blank'
                    >
                        Privacy&nbsp;Policy
                    </CustomLink>
                    .
                </span>
            </div>
            <div className={styles.footer}>
                <div className={styles.footerWrapper}>
                    <Button
                        type='sunset'
                        label='Continue'
                        isLoading={isPending}
                        inSlide
                        buttonProps={{ type: 'submit' }}
                    />
                </div>
            </div>
        </form>
    );
}

EmailSlideV10.propTypes = {
    setEmail: PropTypes.func.isRequired,
    onContinue: PropTypes.func.isRequired,
    results: PropTypes.shape({ version: PropTypes.string, userResponses: PropTypes.array }).isRequired,
    spriteVersion: PropTypes.string.isRequired,
    legalLinks: PropTypes.object
};

export {
    EmailSlideV10
};
